"use client";

import type { FC } from "react";

import {
  TABS_DEFAULTS,
  type TTabsProps,
  type TTabsVariant,
} from "@/ui/components/navigation/Tabs/Tabs.config";
import { TabsContent } from "@/ui/components/navigation/Tabs/TabsContent";
import { TabsList } from "@/ui/components/navigation/Tabs/TabsList";
import { TabsLinks } from "@/ui/components/navigation/Tabs/TabsLinks";
import { TabsRoot } from "@/ui/components/navigation/Tabs/TabsRoot";
import { TabsTriggers } from "@/ui/components/navigation/Tabs/TabsTriggers";
import { useTabs } from "@/ui/components/navigation/Tabs/useTabs";
import { cn } from "@/ui/lib/utils";

export const Tabs: FC<TTabsProps> = ({
  activationMode,
  className,
  contentClassName,
  defaultValue,
  id,
  isTabNavigationDisabled,
  links,
  listClassName,
  onAttemptToNavigateDiabledTabsMessage,
  onValueChange,
  tabs,
  triggerClassName,
  value,
  variant: configuredVariant,
  hideTabList = TABS_DEFAULTS.hideTabList,
  ...props
}) => {
  const {
    handleValueChange,
    selectedValue,
    variant,
  } = useTabs({
    defaultValue,
    id,
    isTabNavigationDisabled,
    onAttemptToNavigateDiabledTabsMessage,
    onValueChange,
    tabs,
    value,
    variant: configuredVariant,
  });

  return (
    <TabsRoot
      data-component="Tabs"
      activationMode={isTabNavigationDisabled ? "manual" : activationMode}
      className={cn("w-full", className)}
      id={id}
      onValueChange={handleValueChange}
      value={`${selectedValue}`}
      {...props}
    >
      <TabsList
        variant={variant}
        className={listClassName}
        hidden={hideTabList}
      >
        { configuredVariant === "icon"
          ? (
            <TabsTriggers
              tabs={tabs}
              triggerClassName={triggerClassName}
              variant="icon"
            />
          )
          : (
            <TabsTriggers
              tabs={tabs}
              triggerClassName={triggerClassName}
              variant={variant as Exclude<TTabsVariant, "icon">}
            />
          ) }
        <TabsLinks links={links} />
      </TabsList>
      { tabs.map((tab) => (
        <TabsContent
          key={tab.value}
          value={`${tab.value}`}
          variant={variant}
          className={contentClassName}
        >
          { tab.content }
        </TabsContent>
      )) }
    </TabsRoot>
  );
};

Tabs.displayName = "Tabs";
